import { useEffect, useRef, type RefObject } from 'react';

/** The id both practice shells give their `<main>`; the skip link targets it. */
export const MAIN_CONTENT_ID = 'content';

/** First focusable element of a shell: jumps a keyboard reader past the rail (UXWALK P1-21). */
export function SkipLink() {
  return (
    <a
      className="vx-skip-link"
      href={`#${MAIN_CONTENT_ID}`}
      onClick={(e) => {
        // A hash link would change the route's URL; move focus instead.
        e.preventDefault();
        const main = document.getElementById(MAIN_CONTENT_ID);
        if (!main) return;
        main.focus();
        main.scrollIntoView({ block: 'start' });
      }}
    >
      Skip to content
    </a>
  );
}

/**
 * On a section change, keep watching until the new section's heading exists, then
 * move focus there so a screen-reader user hears the new page instead of staying on
 * the rail item. The first render never steals focus.
 */
export function useFocusOnSectionChange(section: string, mainRef: RefObject<HTMLElement | null>): void {
  // The section last seen, not a first-run flag: StrictMode runs effects twice on mount.
  const seen = useRef(section);
  useEffect(() => {
    if (seen.current === section) return;
    seen.current = section;
    let observer: MutationObserver | null = null;
    const attempt = () => {
      const main = mainRef.current;
      if (!main) return;
      // A deep link (a Still-needed item, the activity match card) focuses its own
      // target inside the section; never take focus back from it to the page heading.
      const active = document.activeElement;
      if (active && active !== main && main.contains(active)) {
        observer?.disconnect();
        return;
      }
      // Section chunks and their data can take longer than a frame budget.
      // Observe the actual section tree so focus moves only after its heading
      // exists, even on a slow connection.
      const heading = main.querySelector<HTMLElement>('h1, h2');
      if (heading) {
        observer?.disconnect();
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
      }
    };
    const main = mainRef.current;
    if (main) {
      observer = new MutationObserver(attempt);
      observer.observe(main, { childList: true, subtree: true });
    }
    attempt();
    return () => {
      observer?.disconnect();
    };
  }, [section, mainRef]);
}
